import { inject, Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Amplify } from 'aws-amplify'
import {
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  getCurrentUser,
  rememberDevice,
  resetPassword,
  signIn,
  SignInOutput,
  signOut,
} from 'aws-amplify/auth'
import { BehaviorSubject } from 'rxjs'
import { environment } from '../../../environments/environment'

export interface AuthUser {
  username: string
  email?: string
  sub: string
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly router = inject(Router)
  private readonly currentUserSubject = new BehaviorSubject<AuthUser | null>(null)
  readonly currentUser$ = this.currentUserSubject.asObservable()

  constructor() {
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: environment.auth.userPoolId,
          userPoolClientId: environment.auth.userPoolClientId,
        },
      },
    })
  }

  async initializeAuth(): Promise<void> {
    await this.checkAuthState()
  }

  private async checkAuthState(): Promise<void> {
    try {
      const session = await fetchAuthSession()
      const user = await getCurrentUser()
      const idToken = session.tokens?.idToken?.payload
      this.currentUserSubject.next({
        username: user.username,
        email: idToken?.email as string | undefined,
        sub: user.userId,
      })
    } catch {
      this.currentUserSubject.next(null)
    }
  }

  async login(email: string, password: string): Promise<SignInOutput> {
    const result = await signIn({ username: email, password })
    if (result.isSignedIn) {
      await this.checkAuthState()
    }
    return result
  }

  async confirmNewPassword(newPassword: string): Promise<SignInOutput> {
    const result = await confirmSignIn({ challengeResponse: newPassword })
    if (result.isSignedIn) {
      await this.checkAuthState()
    }
    return result
  }

  async confirmSignInWithTOTP(code: string): Promise<SignInOutput> {
    return confirmSignIn({ challengeResponse: code.trim() })
  }

  async rememberDevice(): Promise<void> {
    await rememberDevice()
  }

  async resetPassword(email: string): Promise<void> {
    await resetPassword({ username: email })
  }

  async confirmResetPassword(email: string, confirmationCode: string, newPassword: string): Promise<void> {
    await confirmResetPassword({ username: email, confirmationCode, newPassword })
  }

  async logout(): Promise<void> {
    await signOut()
    this.currentUserSubject.next(null)
    await this.router.navigate(['/login'])
  }

  async getIdToken(): Promise<string | undefined> {
    try {
      const session = await fetchAuthSession()
      return session.tokens?.idToken?.toString()
    } catch {
      return undefined
    }
  }

  async isAuthenticated(): Promise<boolean> {
    await this.checkAuthState()
    return !!this.currentUserSubject.value
  }

  get currentUser(): AuthUser | null {
    return this.currentUserSubject.value
  }
}
